// src/pages/VenueDetailPage.js
import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { useParams } from 'react-router-dom';
import './VenueDetailPage.css';

const GET_VENUE_BY_ID = gql`
  query GetVenueById($id: ID!) {
    getVenueById(id: $id) {
      id
      name
      slug
      address
      city
      state
      postalCode
      country
      latitude
      longitude
      description
      profileImage
      facilities
      weekdayHours
      weekendHours
      holidayHours
      phone
      email
      website
      photos
      capacity
      accessibilityFeatures
      bookingPolicy
      contactPerson
      averageRating
      reviews
      amenities
      upcomingEvents
      socialMediaLinks
      tags
      VenueAreas {
        id
        name
        description
        imageUrl
        isOutdoor
        capacity
      }
    }
  }
`;

const VenueDetailPage = () => {
  const { id } = useParams();
  const { loading, error, data } = useQuery(GET_VENUE_BY_ID, {
    variables: { id },
  });

  if (loading) return <p>Loading venue...</p>;
  if (error) return <p>Error loading venue: {error.message}</p>;

  const venue = data.getVenueById;
  if (!venue) {
    return (
      <div className="artist-page-container" style={{ color: 'white', padding: '2rem' }}>
        <h2>Venue Not Found</h2>
        <p>This venue does not exist or may have been removed.</p>
        <p>Please check the URL or return to the <a href="/" style={{ color: '#61dafb' }}>home page</a>.</p>
      </div>
    );
  }

  return (
    <div className="venue-detail">
      <h1>{venue.name}</h1>
      <img
        src={venue.profileImage || '/default-venue.jpg'}
        alt={venue.name}
        className="venue-profile-img"
      />
      <p><strong>Address:</strong> {venue.address}, {venue.city}, {venue.state} {venue.postalCode}, {venue.country}</p>
      <p><strong>Description:</strong> {venue.description}</p>
      <p><strong>Phone:</strong> {venue.phone}</p>
      <p><strong>Email:</strong> {venue.email}</p>
      <p><strong>Website:</strong> <a href={venue.website} target="_blank" rel="noopener noreferrer">{venue.website}</a></p>
      <p><strong>Capacity:</strong> {venue.capacity}</p>
      <p><strong>Booking Policy:</strong> {venue.bookingPolicy}</p>
      <p><strong>Contact Person:</strong> {venue.contactPerson}</p>
      <p><strong>Hours:</strong> Weekdays: {venue.weekdayHours}, Weekends: {venue.weekendHours}, Holidays: {venue.holidayHours}</p>

      {venue.facilities && <p><strong>Facilities:</strong> {venue.facilities.join(', ')}</p>}
      {venue.accessibilityFeatures && <p><strong>Accessibility Features:</strong> {venue.accessibilityFeatures.join(', ')}</p>}
      {venue.tags && <p><strong>Tags:</strong> {venue.tags.join(', ')}</p>}

      {venue.photos && venue.photos.length > 0 && (
        <div className="venue-photos">
          <h3>Photos</h3>
          {venue.photos.map((url, index) => (
            <img key={index} src={url} alt={`Venue photo ${index + 1}`} className="venue-photo" />
          ))}
        </div>
      )}

      {venue.socialMediaLinks && (
        <div className="venue-social">
          <h3>Social Media</h3>
          {Object.entries(venue.socialMediaLinks).map(([platform, link]) => (
            <p key={platform}><a href={link} target="_blank" rel="noopener noreferrer">{platform}</a></p>
          ))}
        </div>
      )}

      {venue.upcomingEvents && venue.upcomingEvents.length > 0 && (
        <div className="venue-events">
          <h3>Upcoming Events</h3>
          <ul>
            {venue.upcomingEvents.map((event, i) => (
              <li key={i}>{event}</li>
            ))}
          </ul>
        </div>
      )}

      {venue.VenueAreas && venue.VenueAreas.length > 0 && (
        <div className="venue-areas">
          <h2>Areas</h2>
          {venue.VenueAreas.map((area) => (
            <div key={area.id} className="venue-area-card">
              <img
                src={area.imageUrl || '/default-area.jpg'}
                alt={area.name}
                className="venue-area-img"
              />
              <div>
                <h3>{area.name}</h3>
                <p>{area.description}</p>
                <p><strong>Capacity:</strong> {area.capacity || 'Unknown'} | <strong>{area.isOutdoor ? 'Outdoor' : 'Indoor'}</strong></p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VenueDetailPage;
